// Logischer Rechnungsempfänger für Entwurf-Gruppierung und Merge (carry|-Keys).

function nz(v) {
  return String(v ?? "").trim();
}

function normalizePid(id) {
  return nz(id);
}

function lowerEmail(email) {
  return nz(email).toLowerCase();
}

function digitsForPhoneMatch(phone) {
  let s = nz(phone).replace(/[^\d+]/g, "");
  if (s.startsWith("+")) s = s.slice(1);
  s = s.replace(/\D/g, "");
  if (s.startsWith("00")) s = s.slice(2);
  return s;
}

export function billingContactReachabilityFingerprint(contact) {
  const c = contact && typeof contact === "object" ? contact : {};
  const em = lowerEmail(c.email);
  const ph = digitsForPhoneMatch(c.phone);
  if (em && ph) return `e:${em}|p:${ph}`;
  if (em) return `e:${em}`;
  if (ph && ph.length >= 10) return `p:${ph}`;
  return "";
}

export function normalizedBillingContactDisplayName(contact) {
  const c = contact && typeof contact === "object" ? contact : {};
  return `${nz(c.first_name)} ${nz(c.last_name)}`
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function billingContactsMergeableForStatements(a, b) {
  const ida = normalizePid(a?.id);
  const idb = normalizePid(b?.id);
  if (!ida || !idb || ida === idb) return false;

  const na = normalizedBillingContactDisplayName(a);
  const nb = normalizedBillingContactDisplayName(b);
  if (na.length < 2 || na !== nb) return false;

  const ea = lowerEmail(a?.email);
  const eb = lowerEmail(b?.email);
  const pa = digitsForPhoneMatch(a?.phone);
  const pb = digitsForPhoneMatch(b?.phone);
  const paOk = pa.length >= 10;
  const pbOk = pb.length >= 10;

  const fpa = billingContactReachabilityFingerprint(a);
  const fpb = billingContactReachabilityFingerprint(b);

  if (fpa && fpb && fpa === fpb) return true;
  if (ea && eb && ea === eb) return true;
  if (paOk && pbOk && pa === pb) return true;
  if (!fpa && !fpb) return true;

  if (fpa && !fpb && !eb && !pbOk) return true;
  if (fpb && !fpa && !ea && !paOk) return true;

  return false;
}

export function buildBillingContactCanonicalIdMap(contacts) {
  const list = Array.isArray(contacts) ? contacts : [];
  const ids = [
    ...new Set(list.map((c) => normalizePid(c?.id)).filter(Boolean)),
  ];
  if (ids.length < 2) return new Map();

  const parent = new Map(ids.map((id) => [id, id]));

  function findRoot(x) {
    const p = parent.get(x) ?? x;
    if (p !== x) {
      const r = findRoot(p);
      parent.set(x, r);
      return r;
    }
    return x;
  }

  function union(a, b) {
    const ra = findRoot(a);
    const rb = findRoot(b);
    if (ra === rb) return;
    const small = ra.localeCompare(rb) < 0 ? ra : rb;
    const big = ra.localeCompare(rb) < 0 ? rb : ra;
    parent.set(big, small);
  }

  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      if (billingContactsMergeableForStatements(list[i], list[j])) {
        union(normalizePid(list[i].id), normalizePid(list[j].id));
      }
    }
  }

  const out = new Map();
  for (const id of ids) {
    const root = findRoot(id);
    if (root && root !== id) out.set(id, root);
  }
  return out;
}

export function normalizeInvoiceRecipientType(person) {
  const raw = nz(person?.invoice_recipient_type).toLowerCase();
  const hasContact = Boolean(normalizePid(person?.billing_contact_id));
  if (raw === "contact" && hasContact) return "contact";
  return "self";
}

function canonicalBillingContactIdForMatch(rawId, contactCanonicalById) {
  const x = normalizePid(rawId);
  if (!x) return "";
  return contactCanonicalById?.get(x) || x;
}

function findBillingRowById(rows, id) {
  const x = normalizePid(id);
  if (!x) return null;
  return (
    (Array.isArray(rows) ? rows : []).find(
      (row) => normalizePid(row?.id) === x,
    ) || null
  );
}

/**
 * @param {object} statement
 * @param {object[]} [billingContacts]
 * @param {object[]} [people]
 * @returns {{ kind: "contact"|"person", id: string }}
 */
export function resolveBillingRecipientCarriedGroupParts(
  statement,
  billingContacts,
  people = [],
) {
  const st = statement && typeof statement === "object" ? statement : {};
  const rt = nz(st.recipient_type) === "contact" ? "contact" : "person";
  const contacts = Array.isArray(billingContacts) ? billingContacts : [];
  const ppl = Array.isArray(people) ? people : [];
  const contactCanon = buildBillingContactCanonicalIdMap(contacts);

  const canonicalContactIdSet = new Set();
  for (const c of contacts) {
    const cid = canonicalBillingContactIdForMatch(c?.id, contactCanon);
    if (cid) canonicalContactIdSet.add(cid);
  }

  if (rt === "contact") {
    return {
      kind: "contact",
      id: canonicalBillingContactIdForMatch(
        st.billing_contact_id,
        contactCanon,
      ),
    };
  }

  const pid = normalizePid(st.person_id);
  const pidAsContact =
    canonicalBillingContactIdForMatch(pid, contactCanon) || pid;
  if (pid && canonicalContactIdSet.has(pidAsContact)) {
    return { kind: "contact", id: pidAsContact };
  }

  const personRow =
    findBillingRowById(ppl, pid) || findBillingRowById(contacts, pid);
  if (normalizeInvoiceRecipientType(personRow) === "contact") {
    const linkedCid = normalizePid(personRow?.billing_contact_id);
    if (linkedCid) {
      const canon = canonicalBillingContactIdForMatch(linkedCid, contactCanon);
      if (canon) return { kind: "contact", id: canon };
    }
  }

  return { kind: "person", id: pid };
}

export function billingDraftRecipientCarriedGroupKey(
  statement,
  billingContacts,
  people = [],
) {
  const parts = resolveBillingRecipientCarriedGroupParts(
    statement,
    billingContacts,
    people,
  );
  if (parts.kind === "contact" && parts.id) return `carry|contact||${parts.id}`;
  return `carry|person|${parts.id}|`;
}

export function billingRecipientsShareCarriedGroup(
  headerStatement,
  lineStatement,
  billingContacts,
  people = [],
) {
  const contacts = Array.isArray(billingContacts) ? billingContacts : [];
  const ppl = Array.isArray(people) ? people : [];
  const headKey = billingDraftRecipientCarriedGroupKey(
    headerStatement,
    contacts,
    ppl,
  );
  const lineKey = billingDraftRecipientCarriedGroupKey(
    lineStatement,
    contacts,
    ppl,
  );
  return Boolean(headKey && lineKey && headKey === lineKey);
}

/**
 * Bevorzugt Kontakt-Empfänger für aggregierten Versand / Vorschau (Anrede, PDF-Kopf).
 * @param {object[]} statements
 */
export function pickPreferredBillingStatementForAggregatedSend(statements) {
  const list = (Array.isArray(statements) ? statements : []).filter(
    (st) => st && typeof st === "object",
  );
  if (!list.length) return null;
  const contactDraft = list.find(
    (st) =>
      nz(st.recipient_type) === "contact" &&
      normalizePid(st.billing_contact_id),
  );
  return contactDraft || list[0];
}

/**
 * Legacy-Entwürfe: Spieler mit Rechnungsempfänger-Kontakt → Header auf contact umstellen.
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {object} statement
 */
export async function ensureBillingStatementCanonicalRecipientHeader(
  supabase,
  statement,
) {
  const sb = supabase;
  const st = statement && typeof statement === "object" ? statement : null;
  const sid = normalizePid(st?.id);
  if (!sb || !sid) return { ok: false, reason: "missing-input" };

  if (
    nz(st.recipient_type) === "contact" &&
    normalizePid(st.billing_contact_id)
  ) {
    return { ok: true, data: st, changed: false };
  }

  const pid = normalizePid(st.person_id);
  if (!pid) return { ok: true, data: st, changed: false };

  const { data: person, error: pErr } = await sb
    .from("club_people")
    .select("id,billing_contact_id,invoice_recipient_type")
    .eq("id", pid)
    .maybeSingle();
  if (pErr) return { ok: false, reason: "db-error", error: pErr };
  if (!person || normalizeInvoiceRecipientType(person) !== "contact") {
    return { ok: true, data: st, changed: false };
  }

  const cid = normalizePid(person.billing_contact_id);
  if (!cid) return { ok: true, data: st, changed: false };

  const patch = {
    recipient_type: "contact",
    person_id: null,
    billing_contact_id: cid,
  };
  const { data, error } = await sb
    .from("trainerstunden_billing_statements")
    .update(patch)
    .eq("id", sid)
    .select("*")
    .maybeSingle();
  if (error) return { ok: false, reason: "db-error", error };
  return { ok: true, data: data || { ...st, ...patch }, changed: true };
}
